import React, { useState } from "react";
import { ExecutionStep } from "../../../types/ExecutionJob/ExecutionStep.ts";
import { Accordion, AccordionDetails, AccordionSummary, Chip, IconButton, Stack, Typography } from "@mui/material";
import { CloseRounded, EditRounded, ExpandMoreRounded, RestoreFromTrashRounded } from "@mui/icons-material";
import {
  getExecutionStepDescription,
  getExecutionStepEditDrawerConfig,
  getExecutionStepIcon,
} from "../../../services/executionJobService";
import Grid from "@mui/material/Grid";
import { translate } from "../../../services/Translations/translatorService";
import { useAiChat } from "../../../provider/AiChatProvider";
import { useMutation } from "@tanstack/react-query";
import { updateResource } from "../../../services/ReactQuery/reactQueryService";
import { useSnackbar } from "notistack";
import { useCase } from "../../../provider/CaseProvider";
import useEffectWithoutMount from "../../../hooks/useEffectWithoutMount.ts";

type ExecutionStepAccordionProps = {
  step: ExecutionStep;
  refreshAiChatMessages: () => Promise<void>;
};

export default function ExecutionStepAccordion({ step, refreshAiChatMessages }: ExecutionStepAccordionProps) {
  const [expanded, setExpanded] = useState(false);
  const { activeEditStepId, openStepEditDrawer, closeStepEditDrawer } = useAiChat();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: refreshAiChatMessages,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { product } = useCase();

  useEffectWithoutMount(() => {
    if (activeEditStepId === step.id && step.payload.lastEditedBy === "ai") {
      openOrUpdateStepDrawer();
    }
  }, [step]);

  if (!product) {
    return null;
  }

  const editableActionTypes = [
    "createActivity",
    "createNote",
    "sendCustomerMessage",
    "sendEmail",
    "sendBeaOrFax",
    "createInquiryToCustomer",
    "createDocument",
  ];

  const Icon = getExecutionStepIcon(step.actionType);
  const isDeleted = step.status === "skipped";

  const updateStepStatus = async (status: "skipped" | "pending") => {
    if (status === "skipped" && activeEditStepId === step.id) {
      closeStepEditDrawer();
    }
    await updateMutation.mutateAsync({
      uri: "execution_steps",
      id: step.id,
      data: {
        status: status,
      },
    });
    enqueueSnackbar(status === "skipped" ? "Schritt wurde übersprungen." : "Schritt wurde wieder aktiviert.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  };

  const openOrUpdateStepDrawer = () => {
    const drawerConfig = getExecutionStepEditDrawerConfig(step, updateMutation);
    if (!drawerConfig) {
      return;
    }
    openStepEditDrawer(step.id, drawerConfig);
  };

  const isEditDrawerOpenForStep = activeEditStepId === step.id;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => !isDeleted && setExpanded((prev) => !prev)}
      disableGutters
      elevation={0}
      variant={"outlined"}
      sx={{
        backgroundColor: isDeleted ? "action.hover" : "background.paper",
        ...(isEditDrawerOpenForStep
          ? {
              backgroundColor: "action.hover",
              border: "2px solid",
              borderColor: "primary.main",
            }
          : {}),
      }}
    >
      <AccordionSummary expandIcon={isDeleted ? null : <ExpandMoreRounded />}>
        <Grid container sx={{ alignItems: "center", width: "100%" }}>
          <Grid size={1}>
            <Icon fontSize="small" color={isDeleted ? "disabled" : "primary"} />
          </Grid>
          <Grid size={8}>
            <Typography variant="subtitle2" color="text.primary">
              {translate(`executionJob.step.actionType.values.${step.actionType}`)}
            </Typography>
          </Grid>
          <Grid size={3}>
            <Stack direction={"row"} sx={{ justifyContent: "flex-end" }}>
              {!isDeleted && step.status === "pending" && editableActionTypes.includes(step.actionType) && (
                <IconButton
                  size={"small"}
                  onClick={(event) => {
                    event.stopPropagation();
                    openOrUpdateStepDrawer();
                  }}
                >
                  <EditRounded fontSize={"small"} />
                </IconButton>
              )}
              {isDeleted && (
                <IconButton
                  size={"small"}
                  onClick={async (event) => {
                    event.stopPropagation();
                    await updateStepStatus("pending");
                  }}
                >
                  <RestoreFromTrashRounded fontSize={"small"} />
                </IconButton>
              )}
              {!isDeleted && (
                <IconButton
                  size={"small"}
                  onClick={async (event) => {
                    event.stopPropagation();
                    await updateStepStatus("skipped");
                  }}
                >
                  <CloseRounded fontSize={"small"} />
                </IconButton>
              )}
            </Stack>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant={"body2"} color={"text.secondary"}>
          {getExecutionStepDescription(step)}
        </Typography>
        <Chip size={"small"} label={translate(`executionJob.step.status.values.${step.status}`)} />
      </AccordionDetails>
    </Accordion>
  );
}
